import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import DOMPurify from 'dompurify';
import { theme, neutral, blueGrey } from '../../design-system/theme';
import { Template, Project, DataCollector } from '../../types/admin';
import {
  FormGroup,
  Label,
  Input,
  Select,
  Textarea,
  TabContainer,
  Tab as SharedTab,
} from '../../styles/adminShared';

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  height: calc(100vh - 200px);
  min-height: 600px;
  
  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
    height: auto;
  }
`;

const EditorPanel = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const PreviewPanel = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const PanelHeader = styled.div`
  padding: 16px 24px;
  background: ${neutral[2]};
  border-bottom: 1px solid ${neutral[3]};
  font-weight: 600;
  color: ${theme.textPrimary};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PanelBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;

const TabsContainer = styled(TabContainer)`
  background: ${neutral[2]};
  padding: 8px;
  margin-bottom: 0;
  border-bottom: 1px solid ${neutral[3]};
`;

const TabButton = styled(SharedTab)`
  padding: 10px 16px;
  font-size: 13px;
  background: ${props => props.$active ? 'white' : 'transparent'};
  border-radius: 8px;
  
  &:hover {
    background: ${props => props.$active ? 'white' : neutral[2]};
  }
`;

const HtmlEditor = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 2px solid ${neutral[3]};
  border-radius: 8px;
  font-size: 13px;
  min-height: 200px;
  resize: vertical;
  font-family: 'SF Mono', 'Monaco', 'Consolas', monospace;
  background: #1e1e2e;
  color: #cdd6f4;
  
  &:focus {
    outline: none;
    border-color: ${theme.primary};
  }
`;

const FormRowTwo = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ColorInput = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const ColorPicker = styled.input`
  width: 50px;
  height: 40px;
  padding: 0;
  border: 2px solid ${neutral[3]};
  border-radius: 8px;
  cursor: pointer;
`;

const ColorHex = styled(Input)`
  flex: 1;
`;

const Section = styled.div`
  border: 1px solid ${neutral[3]};
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  background: ${neutral[1]};
`;

const SectionTitle = styled.h4`
  margin: 0 0 16px 0;
  color: ${blueGrey[8]};
  font-size: 15px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const CheckboxLabel = styled.label<{ $checked?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: ${props => props.$checked ? '#e8f4f8' : neutral[2]};
  border: 2px solid ${props => props.$checked ? theme.primary : neutral[3]};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;
  
  &:hover {
    border-color: ${theme.primary};
  }
  
  input {
    margin: 0;
  }
`;

const PreviewFrame = styled.div<{ $primaryColor?: string; $secondaryColor?: string }>`
  border: 8px solid #1e293b;
  border-radius: 24px;
  overflow: hidden;
  background: white;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  max-width: 380px;
  margin: 0 auto;
  
  .preview-header {
    padding: 20px;
    background: ${props => props.$primaryColor || theme.primary};
    color: white;
    display: flex;
    align-items: center;
    gap: 16px;
    
    img {
      width: 48px;
      height: 48px;
      border-radius: 8px;
      object-fit: contain;
      background: white;
    }
    
    h3 {
      margin: 0;
      font-size: 18px;
    }
    
    p {
      margin: 4px 0 0 0;
      font-size: 13px;
      opacity: 0.9;
    }
  }
  
  .preview-body {
    padding: 24px;
    
    .consent-section {
      background: ${neutral[2]};
      border-radius: 8px;
      padding: 20px;
      margin-bottom: 16px;
      
      h4 {
        margin: 0 0 12px 0;
        color: ${blueGrey[8]};
        font-size: 15px;
      }
    }
    
    .data-categories {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-bottom: 16px;
      
      span {
        background: ${props => props.$secondaryColor || '#e8f4f8'};
        color: #2980b9;
        padding: 6px 12px;
        border-radius: 6px;
        font-size: 12px;
      }
    }
  }
  
  .preview-footer {
    padding: 20px;
    border-top: 1px solid ${neutral[3]};
    background: ${neutral[2]};
    
    .signature-box {
      border: 2px dashed ${neutral[4]};
      border-radius: 8px;
      padding: 40px;
      text-align: center;
      color: ${theme.textSecondary};
      margin-bottom: 16px;
    }
    
    .buttons {
      display: flex;
      gap: 12px;
      justify-content: flex-end;
      
      button {
        padding: 12px 24px;
        border-radius: 8px;
        font-weight: 600;
        cursor: pointer;
        font-size: 14px;
        
        &.accept {
          background: ${props => props.$primaryColor || theme.primary};
          color: white;
          border: none;
        }
        
        &.reject {
          background: white;
          border: 2px solid ${neutral[3]};
          color: ${theme.textSecondary};
        }
      }
    }
  }
`;

const HTMLPreview = styled.div`
  padding: 16px;
  background: ${neutral[2]};
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.6;
  
  h1, h2, h3, h4 { margin-top: 0; color: ${theme.textPrimary}; }
  p { margin: 0 0 12px 0; }
  ul, ol { margin: 0 0 12px 0; padding-left: 24px; }
`;

const ActionButtonsGroup = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${neutral[3]};
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const CancelButton = styled.button`
  padding: 12px 24px;
  background: ${theme.textSecondary};
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
`;

const SaveButton = styled.button`
  padding: 12px 24px;
  background: ${props => props.disabled ? neutral[4] : theme.primary};
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
`;

interface TemplateBuilderProps {
  template?: Template | null;
  projects: Project[];
  dataCollectors: DataCollector[];
  onSave: (template: Partial<Template>) => void;
  onCancel: () => void;
}

interface BrandingConfig {
  primaryColor: string;
  secondaryColor: string;
  logoUrl: string;
  headerText: string;
  footerText: string;
}

interface TemplateFormData {
  name: string;
  description: string;
  projectId: string;
  dataCollectorId: string;
  dataCategories: string[];
  
  // Documents
  consentText: string;
  privacyPolicyText: string;
  termsText: string;
  
  // Branding
  branding: BrandingConfig;
  
  // Settings
  evidenceTypes: string[];
  expiryDays: number;
  requireReconsent: boolean;
  
  // Disclosure
  primaryPurposes: string[];
  retentionDays: number;
  
  // Document types to include
  documentTypes: string[];
}

const DATA_CATEGORIES = [
  'Personal Information',
  'Contact Details',
  'Financial Information',
  'Health & Medical Data',
  'Biometric Data',
  'Location Data',
  'Behavioral Data',
  'Communication Data',
  'Employment Information',
  'Educational Information',
];

const EVIDENCE_TYPES = [
  { id: 'SIGNATURE', label: 'Digital Signature', icon: '✍️' },
  { id: 'PHONE_OTP', label: 'Phone OTP', icon: '📱' },
  { id: 'EMAIL_OTP', label: 'Email OTP', icon: '📧' },
  { id: 'VIDEO', label: 'Video Recording', icon: '🎥' },
  { id: 'AUDIO', label: 'Audio Recording', icon: '🎙️' },
  { id: 'CHECKBOX', label: 'Checkbox Confirmation', icon: '☑️' },
];

// Consent Document Types aligned with backend ConsentDocumentType enum
const CONSENT_DOCUMENT_TYPES = [
  { id: 'PRIVACY_POLICY', label: 'Privacy Policy', icon: '🔒', description: 'Data handling disclosure' },
  { id: 'CONSENT_TEXT', label: 'Consent Text', icon: '✍️', description: 'Primary consent agreement' },
  { id: 'TERMS_OF_SERVICE', label: 'Terms of Service', icon: '📋', description: 'Legal usage terms' },
  { id: 'DATA_SHARING_AGREEMENT', label: 'Data Sharing Agreement', icon: '🤝', description: 'Third-party sharing terms' },
  { id: 'COOKIE_POLICY', label: 'Cookie Policy', icon: '🍪', description: 'Website tracking disclosure' },
  { id: 'RETENTION_POLICY', label: 'Data Retention Policy', icon: '📅', description: 'Data lifecycle information' },
  { id: 'GDPR_NOTICE', label: 'GDPR Notice', icon: '🇪🇺', description: 'EU data protection rights' },
  { id: 'HIPAA_AUTHORIZATION', label: 'HIPAA Authorization', icon: '🏥', description: 'Healthcare data consent' },
  { id: 'CCPA_DISCLOSURE', label: 'CCPA Disclosure', icon: '🇺🇸', description: 'California privacy rights' },
  { id: 'AADHAAR_AUTHORIZATION', label: 'Aadhaar Authorization', icon: '🪪', description: 'India ID-linked consent' },
  { id: 'CUSTOM', label: 'Custom Document', icon: '📄', description: 'Custom requirements' },
];

const DEFAULT_BRANDING: BrandingConfig = {
  primaryColor: theme.primary,
  secondaryColor: '#e8f4f8',
  logoUrl: '',
  headerText: '',
  footerText: '',
};

export const TemplateBuilder: React.FC<TemplateBuilderProps> = ({
  template,
  projects,
  dataCollectors,
  onSave,
  onCancel,
}) => {
  const [activeTab, setActiveTab] = useState<'basic' | 'content' | 'branding' | 'settings'>('basic');
  const [formData, setFormData] = useState<TemplateFormData>({
    name: template?.name || '',
    description: template?.description || '',
    projectId: template?.projectId || '',
    dataCollectorId: template?.dataCollectorId || '',
    dataCategories: template?.dataCategories || [],
    consentText: template?.consentText || getDefaultConsentText(),
    privacyPolicyText: '',
    termsText: '',
    branding: DEFAULT_BRANDING,
    evidenceTypes: template?.evidenceTypes || ['SIGNATURE'],
    expiryDays: template?.expiryDays || 365,
    requireReconsent: template?.requireReconsent || false,
    primaryPurposes: [],
    retentionDays: 365,
    documentTypes: ['CONSENT_TEXT', 'PRIVACY_POLICY'], // Default document types
  });
  
  // Reset form data when template prop changes (for edit mode)
  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name || '',
        description: template.description || '',
        projectId: template.projectId || '',
        dataCollectorId: template.dataCollectorId || '',
        dataCategories: template.dataCategories || [],
        consentText: template.consentText || getDefaultConsentText(),
        privacyPolicyText: '',
        termsText: '',
        branding: DEFAULT_BRANDING,
        evidenceTypes: template.evidenceTypes || ['SIGNATURE'],
        expiryDays: template.expiryDays || 365,
        requireReconsent: template.requireReconsent || false,
        primaryPurposes: [],
        retentionDays: 365,
        documentTypes: ['CONSENT_TEXT', 'PRIVACY_POLICY'],
      });
      setActiveTab('basic');
    }
  }, [template]);
  
  const selectedDataCollector = dataCollectors.find(dc => dc.dataCollectorId === formData.dataCollectorId);
  
  function getDefaultConsentText(): string {
    return `<h2>Consent</h2>
<p>By providing your consent, you agree to the following:</p>

<h3>Data Collection</h3>
<p>We will collect and process the following categories of your personal data:</p>
<ul>
  <li>Personal identification information</li>
  <li>Contact details</li>
</ul>

<h3>Purpose of Processing</h3>
<p>Your data will be processed for the following purposes:</p>
<ul>
  <li>Providing requested services</li>
  <li>Communication regarding your account</li>
  <li>Compliance with legal obligations</li>
</ul>

<h3>Your Rights</h3>
<p>Under the DPDP Act 2023, you have the right to:</p>
<ul>
  <li>Access your personal data</li>
  <li>Correct inaccurate data</li>
  <li>Request erasure of your data</li>
  <li>Withdraw consent at any time</li>
</ul>`;
  }
  
  const handleSave = () => {
    // Map frontend fields to backend DTO format
    const templateData: Partial<Template> = {
      projectId: formData.projectId,
      dataCollectorId: formData.dataCollectorId,
      dataCategories: formData.dataCategories.length > 0 ? formData.dataCategories : ['GENERAL'],
      evidenceTypes: formData.evidenceTypes.length > 0 ? formData.evidenceTypes : ['SIGNATURE'],
      // Backend requires document flags and content
      hasPrivacyPolicy: formData.documentTypes.includes('PRIVACY_POLICY'),
      privacyPolicyDataType: 'text/html',
      privacyPolicyData: DOMPurify.sanitize(formData.privacyPolicyText || '<p>Privacy Policy</p>'),
      hasConsent: formData.documentTypes.includes('CONSENT_TEXT'),
      consentDataType: 'text/html',
      consentData: DOMPurify.sanitize(formData.consentText || '<p>Consent Text</p>'),
      hasTermOfService: formData.documentTypes.includes('TERMS_OF_SERVICE'),
      termOfServiceDataType: formData.termsText ? 'text/html' : undefined,
      termOfServiceData: formData.termsText ? DOMPurify.sanitize(formData.termsText) : undefined,
      expiryDays: formData.expiryDays,
      requireReconsent: formData.requireReconsent,
    };
    onSave(templateData);
  };
  
  const toggleCategory = (category: string) => {
    setFormData(prev => ({
      ...prev,
      dataCategories: prev.dataCategories.includes(category)
        ? prev.dataCategories.filter(c => c !== category)
        : [...prev.dataCategories, category],
    }));
  };
  
  const toggleEvidence = (evidenceId: string) => {
    setFormData(prev => ({
      ...prev,
      evidenceTypes: prev.evidenceTypes.includes(evidenceId)
        ? prev.evidenceTypes.filter(e => e !== evidenceId)
        : [...prev.evidenceTypes, evidenceId],
    }));
  };

  const toggleDocumentType = (docTypeId: string) => {
    setFormData(prev => ({
      ...prev,
      documentTypes: prev.documentTypes.includes(docTypeId)
        ? prev.documentTypes.filter(d => d !== docTypeId)
        : [...prev.documentTypes, docTypeId],
    }));
  };
  
  return (
    <Container>
      <EditorPanel>
        <PanelHeader>
          ✏️ Template Editor
        </PanelHeader>
        <TabsContainer>
          <TabButton $active={activeTab === 'basic'} onClick={() => setActiveTab('basic')}>
            Basic Info
          </TabButton>
          <TabButton $active={activeTab === 'content'} onClick={() => setActiveTab('content')}>
            Consent Content
          </TabButton>
          <TabButton $active={activeTab === 'branding'} onClick={() => setActiveTab('branding')}>
            Branding
          </TabButton>
          <TabButton $active={activeTab === 'settings'} onClick={() => setActiveTab('settings')}>
            Settings
          </TabButton>
        </TabsContainer>
        <PanelBody>
          {activeTab === 'basic' && (
            <>
              <FormGroup>
                <Label>Template Name *</Label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Patient Registration Consent"
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of this consent template..."
                  style={{ minHeight: '80px' }}
                />
              </FormGroup>
              
              <FormRowTwo>
                <FormGroup>
                  <Label>Project *</Label>
                  <Select
                    value={formData.projectId}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFormData({ ...formData, projectId: e.target.value })}
                  >
                    <option value="">Select a project...</option>
                    {projects.map(p => (
                      <option key={p.projectId} value={p.projectId}>{p.name}</option>
                    ))}
                  </Select>
                </FormGroup>
                
                <FormGroup>
                  <Label>Data Collector *</Label>
                  <Select
                    value={formData.dataCollectorId}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFormData({ ...formData, dataCollectorId: e.target.value })}
                  >
                    <option value="">Select a data collector...</option>
                    {dataCollectors.map(dc => (
                      <option key={dc.dataCollectorId} value={dc.dataCollectorId}>{dc.name}</option>
                    ))}
                  </Select>
                </FormGroup>
              </FormRowTwo>
              
              <Section>
                <SectionTitle>📊 Data Categories</SectionTitle>
                <CheckboxGroup>
                  {DATA_CATEGORIES.map(category => (
                    <CheckboxLabel 
                      key={category}
                      $checked={formData.dataCategories.includes(category)}
                    >
                      <input
                        type="checkbox"
                        checked={formData.dataCategories.includes(category)}
                        onChange={() => toggleCategory(category)}
                      />
                      {category}
                    </CheckboxLabel>
                  ))}
                </CheckboxGroup>
              </Section>
            </>
          )}
          
          {activeTab === 'content' && (
            <>
              {/* Document Types Selection */}
              <Section>
                <SectionTitle>📄 Document Types to Include</SectionTitle>
                <p style={{ fontSize: '13px', color: theme.textSecondary, marginBottom: '12px' }}>
                  Select which documents will be required in this consent form.
                </p>
                <CheckboxGroup>
                  {CONSENT_DOCUMENT_TYPES.map(docType => (
                    <CheckboxLabel 
                      key={docType.id}
                      $checked={formData.documentTypes.includes(docType.id)}
                      title={docType.description}
                    >
                      <input
                        type="checkbox"
                        checked={formData.documentTypes.includes(docType.id)}
                        onChange={() => toggleDocumentType(docType.id)}
                      />
                      <span>{docType.icon}</span>
                      <span style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <span>{docType.label}</span>
                        <span style={{ fontSize: '11px', color: theme.textSecondary }}>{docType.description}</span>
                      </span>
                    </CheckboxLabel>
                  ))}
                </CheckboxGroup>
              </Section>

              <FormGroup>
                <Label>Consent Text (HTML)</Label>
                <p style={{ fontSize: '13px', color: theme.textSecondary, marginBottom: '12px' }}>
                  Write the consent text. HTML formatting is supported.
                </p>
                <HtmlEditor
                  value={formData.consentText}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData({ ...formData, consentText: e.target.value })}
                  placeholder="<h2>Consent</h2>..."
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Privacy Policy Text (HTML)</Label>
                <p style={{ fontSize: '13px', color: theme.textSecondary, marginBottom: '12px' }}>
                  Write the privacy policy text. HTML formatting is supported.
                </p>
                <HtmlEditor
                  value={formData.privacyPolicyText}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData({ ...formData, privacyPolicyText: e.target.value })}
                  placeholder="<h2>Privacy Policy</h2>..."
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Terms of Service Text (HTML)</Label>
                <p style={{ fontSize: '13px', color: theme.textSecondary, marginBottom: '12px' }}>
                  Write the terms of service text. HTML formatting is supported.
                </p>
                <HtmlEditor
                  value={formData.termsText}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData({ ...formData, termsText: e.target.value })}
                  placeholder="<h2>Terms of Service</h2>..."
                />
              </FormGroup>
              
              <Section>
                <SectionTitle>📋 Quick Templates</SectionTitle>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, consentText: getDefaultConsentText() })}
                    style={{
                      padding: '8px 16px',
                      background: neutral[2],
                      border: `1px solid ${neutral[3]}`,
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                    }}
                  >
                    📄 Default DPDP Template
                  </button>
                </div>
              </Section>
            </>
          )}
          
          {activeTab === 'branding' && (
            <>
              <Section>
                <SectionTitle>🎨 Colors</SectionTitle>
                <FormRowTwo>
                  <FormGroup>
                    <Label>Primary Color</Label>
                    <ColorInput>
                      <ColorPicker
                        type="color"
                        value={formData.branding.primaryColor}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({
                          ...formData,
                          branding: { ...formData.branding, primaryColor: e.target.value }
                        })}
                      />
                      <ColorHex
                        type="text"
                        value={formData.branding.primaryColor}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({
                          ...formData,
                          branding: { ...formData.branding, primaryColor: e.target.value }
                        })}
                      />
                    </ColorInput>
                  </FormGroup>
                  
                  <FormGroup>
                    <Label>Secondary Color</Label>
                    <ColorInput>
                      <ColorPicker
                        type="color"
                        value={formData.branding.secondaryColor}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({
                          ...formData,
                          branding: { ...formData.branding, secondaryColor: e.target.value }
                        })}
                      />
                      <ColorHex
                        type="text"
                        value={formData.branding.secondaryColor}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({
                          ...formData,
                          branding: { ...formData.branding, secondaryColor: e.target.value }
                        })}
                      />
                    </ColorInput>
                  </FormGroup>
                </FormRowTwo>
              </Section>
              
              <FormGroup>
                <Label>Logo URL</Label>
                <Input
                  type="url"
                  value={formData.branding.logoUrl}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({
                    ...formData,
                    branding: { ...formData.branding, logoUrl: e.target.value }
                  })}
                  placeholder="https://example.com/logo.png"
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Header Text (Optional)</Label>
                <Input
                  type="text"
                  value={formData.branding.headerText}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({
                    ...formData,
                    branding: { ...formData.branding, headerText: e.target.value }
                  })}
                  placeholder="Custom header message..."
                />
              </FormGroup>
            </>
          )}
          
          {activeTab === 'settings' && (
            <>
              <Section>
                <SectionTitle>🔐 Evidence Collection</SectionTitle>
                <CheckboxGroup>
                  {EVIDENCE_TYPES.map(evidence => (
                    <CheckboxLabel 
                      key={evidence.id}
                      $checked={formData.evidenceTypes.includes(evidence.id)}
                    >
                      <input
                        type="checkbox"
                        checked={formData.evidenceTypes.includes(evidence.id)}
                        onChange={() => toggleEvidence(evidence.id)}
                      />
                      <span>{evidence.icon}</span>
                      {evidence.label}
                    </CheckboxLabel>
                  ))}
                </CheckboxGroup>
              </Section>
              
              <FormRowTwo>
                <FormGroup>
                  <Label>Consent Validity (Days)</Label>
                  <Input
                    type="number"
                    value={formData.expiryDays}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const val = parseInt(e.target.value, 10);
                      const validVal = isNaN(val) || val < 1 || val > 3650 ? 365 : val;
                      setFormData({ ...formData, expiryDays: validVal });
                    }}
                    min="1"
                    max="3650"
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>Retention Period (Days)</Label>
                  <Input
                    type="number"
                    value={formData.retentionDays}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const val = parseInt(e.target.value, 10);
                      const validVal = isNaN(val) || val < 1 || val > 3650 ? 365 : val;
                      setFormData({ ...formData, retentionDays: validVal });
                    }}
                    min="1"
                    max="3650"
                  />
                </FormGroup>
              </FormRowTwo>
              
              <CheckboxLabel $checked={formData.requireReconsent} style={{ marginTop: '16px' }}>
                <input
                  type="checkbox"
                  checked={formData.requireReconsent}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, requireReconsent: e.target.checked })}
                />
                Require reconsent when template is updated
              </CheckboxLabel>
            </>
          )}
        </PanelBody>
        
        <ActionButtonsGroup>
          <CancelButton onClick={onCancel}>
            Cancel
          </CancelButton>
          <SaveButton
            onClick={handleSave}
            disabled={!formData.name || !formData.projectId || !formData.dataCollectorId}
          >
            {template ? 'Update Template' : 'Create Template'}
          </SaveButton>
        </ActionButtonsGroup>
      </EditorPanel>
      
      <PreviewPanel>
        <PanelHeader>
          👁️ Live Preview
        </PanelHeader>
        <PanelBody style={{ background: neutral[2] }}>
          <PreviewFrame 
            $primaryColor={formData.branding.primaryColor}
            $secondaryColor={formData.branding.secondaryColor}
          >
            <div className="preview-header">
              {(formData.branding.logoUrl || selectedDataCollector?.logo) ? (
                <img 
                  src={formData.branding.logoUrl || selectedDataCollector?.logo} 
                  alt="Logo" 
                  onError={(e) => { 
                    if (e.target instanceof HTMLImageElement) {
                      e.target.style.display = 'none';
                    }
                  }}
                />
              ) : (
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '8px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px'
                }}>
                  📋
                </div>
              )}
              <div>
                <h3>{formData.name || 'Consent Form'}</h3>
                <p>{selectedDataCollector?.name || 'Organization Name'}</p>
              </div>
            </div>
            
            <div className="preview-body">
              {formData.dataCategories.length > 0 && (
                <div className="data-categories">
                  {formData.dataCategories.map((cat, i) => (
                    <span key={i}>{cat}</span>
                  ))}
                </div>
              )}
              
              <div className="consent-section">
                <h4>📋 Consent</h4>
                <HTMLPreview dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formData.consentText) }} />
              </div>
            </div>
            
            <div className="preview-footer">
              {formData.evidenceTypes.includes('signature') && (
                <div className="signature-box">
                  ✍️ Draw your signature here
                </div>
              )}
              
              <div className="buttons">
                <button className="reject">Decline</button>
                <button className="accept">Accept & Sign</button>
              </div>
            </div>
          </PreviewFrame>
        </PanelBody>
      </PreviewPanel>
    </Container>
  );
};

export default TemplateBuilder;
